import { Campaign, Template, VariantUpdateParams } from '../../../types'
import Modal from '../../../ui/Modal'
import { DataTable } from '../../../ui/DataTable'
import Button from '../../../ui/Button'
import Tag from '../../../ui/Tag'
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import api from '../../../api'
import { TemplateContext } from '../../../contexts'
import { useTranslation } from 'react-i18next'
import VariantFormModal from './VariantFormModal'

interface VariantEditParams {
    open: boolean
    setIsOpen: (state: boolean) => void
    campaign: Campaign
    setCampaign: Dispatch<SetStateAction<Campaign>>
}

export default function VariantListModal({ open, setIsOpen, campaign, setCampaign }: VariantEditParams) {
    const { t } = useTranslation()
    const { variants, currentTemplate, setTemplate } = useContext(TemplateContext)
    const [editVariant, setEditVariant] = useState<VariantUpdateParams | undefined>()
    const [archivedVariants, setArchivedVariants] = useState<Template[]>([])

    const loadArchived = async () => {
        const templates = await api.campaigns.templates(campaign.project_id, campaign.id, true)
        setArchivedVariants(templates.filter(template => template.deleted_at))
    }

    useEffect(() => {
        if (!open) return
        loadArchived()
    }, [open, campaign.id, campaign.project_id])

    const localeArchived = archivedVariants.filter(template => template.locale === currentTemplate?.locale)

    const handleArchiveVariant = async (id: number) => {
        if (!confirm(t('variant_remove_warning'))) return
        await api.templates.delete(campaign.project_id, id)

        setCampaign(prev => ({ ...prev, templates: prev.templates.filter(template => template.id !== id) }))
        const remaining = campaign.templates.filter(template => template.id !== id && !template.deleted_at)
        setTemplate(remaining.find(template => template.locale === currentTemplate?.locale) ?? remaining[0])
        await loadArchived()
    }

    const handleUnarchiveVariant = async (id: number) => {
        const template = await api.templates.unarchive(campaign.project_id, id)
        setCampaign(prev => ({ ...prev, templates: [...prev.templates, template] }))
        setArchivedVariants(prev => prev.filter(variant => variant.id !== id))
        setTemplate(template)
    }

    const handleViewVariant = (template: Template) => {
        setTemplate(template)
        setIsOpen(false)
    }

    const handleCreateVariant = async (campaign: Campaign, template: Template) => {
        setCampaign(campaign)
        setTemplate(template)
        setEditVariant(undefined)
    }

    const activeVariants = variants.filter(variant => !variant.deleted_at)
    const items = [...activeVariants, ...localeArchived]

    return (
        <Modal title={t('variants')}
            description={t('variants_description')}
            size="regular"
            open={open}
            onClose={() => setIsOpen(false)}>
            { items.length > 1 && (
                <DataTable
                    items={items}
                    itemKey={({ item }) => item.id}
                    onSelectRow={(item) => handleViewVariant(item)}
                    columns={[
                        {
                            key: 'label',
                            title: t('variant'),
                            cell: ({ item }) => item.name,
                        },
                        { key: 'locale', title: t('locale') },
                        {
                            key: 'status',
                            title: t('status'),
                            cell: ({ item }) => item.deleted_at
                                ? <Tag variant="plain" size="tiny">{t('archived')}</Tag>
                                : <Tag variant="success" size="tiny">{t('active')}</Tag>,
                        },
                        {
                            key: 'options',
                            title: t('options'),
                            align: 'end',
                            cell: ({ item }) => (
                                <div style={{ display: 'flex', gap: '5px', justifyContent: 'end' }}>
                                    {item.deleted_at
                                        ? (
                                            <Button
                                                size="small"
                                                variant="secondary"
                                                onClick={async (event) => {
                                                    event.preventDefault()
                                                    event.stopPropagation()
                                                    await handleUnarchiveVariant(item.id)
                                                }}>
                                                {t('unarchive')}
                                            </Button>
                                        )
                                        : (
                                            <>
                                                <Button
                                                    size="small"
                                                    variant="secondary"
                                                    onClick={(event) => {
                                                        event.preventDefault()
                                                        event.stopPropagation()
                                                        setEditVariant(item)
                                                    }}>
                                                    {t('edit')}
                                                </Button>
                                                {activeVariants.length > 1 && (
                                                    <Button
                                                        size="small"
                                                        variant="destructive"
                                                        onClick={async (event) => {
                                                            event.preventDefault()
                                                            event.stopPropagation()
                                                            await handleArchiveVariant(item.id)
                                                        }}>
                                                        {t('archive')}
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                </div>
                            ),
                        },
                    ]} />
            )}
            <div className="modal-footer">
                <Button size="small" onClick={() => setEditVariant({ name: '' })}>{t(variants.length > 1 ? 'variant_add' : 'variant_create')}</Button>
            </div>

            <VariantFormModal
                variant={editVariant}
                onClose={() => setEditVariant(undefined)}
                campaign={campaign}
                onCreate={handleCreateVariant} />
        </Modal>
    )
}
